import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, PhoneCall } from "lucide-react";
import { ASSETS, COMPANY } from "../../data";
import { scrollTo } from "../../hooks/useLenis";

const LINKS = [
  { label: "About", id: "#about" },
  { label: "Product", id: "#product" },
  { label: "Quality", id: "#quality" },
  { label: "Facility", id: "#facility" },
  { label: "Enquiry", id: "#enquiry" },
  { label: "Contact", id: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_1px_20px_rgba(10,17,40,0.05)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("#top")}
          className="flex items-center gap-2 active:scale-95 transition-transform"
        >
          <img src={ASSETS.logo} alt="Polycaps" className="h-11 md:h-12 w-auto object-contain rounded-md" />
        </button>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-[var(--ink)] hover:text-[var(--brand)] transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-[var(--brand)] group-hover:w-full transition-[width] duration-300" />
            </button>
          ))}
          <a
            data-testid="nav-call-btn"
            href={`tel:${COMPANY.phone1raw}`}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[var(--brand-hover)] active:scale-95 transition-[background-color,transform]"
          >
            <PhoneCall size={15} /> Call Us
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-[var(--ink)] active:scale-90 transition-transform"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-white border-b border-slate-200"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.id)}
                  className="text-left py-3 text-lg font-medium border-b border-slate-100 hover:text-[var(--brand)] transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={`tel:${COMPANY.phone1raw}`}
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--brand)] px-5 py-3 text-sm font-semibold text-white"
              >
                <PhoneCall size={16} /> Call {COMPANY.phone1}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
